import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import anime from 'animejs/lib/anime.es.js';
import { ExternalLink } from 'lucide-react';
import MagneticButton from '../components/MagneticButton';

// Wave painting background
const WAVE_BG = 'https://customer-assets.emergentagent.com/job_7ae2fa0d-fc89-4b84-8ee2-77e1b09b9f84/artifacts/97l9ougt_BG_LEZIN_WAVE_03_C.png';

// Partner logos
const LOGOS = {
  'metro-club': 'https://customer-assets.emergentagent.com/job_e3d607ae-98b3-4cfa-9c06-6cf7ab967614/artifacts/m33n80fc_metro%20club.png',
  'leddis': 'https://customer-assets.emergentagent.com/job_e3d607ae-98b3-4cfa-9c06-6cf7ab967614/artifacts/xu85l1hn_LEDDIS%20logo.png',
  'momento': 'https://customer-assets.emergentagent.com/job_e3d607ae-98b3-4cfa-9c06-6cf7ab967614/artifacts/fvnj7on3_Typo%20Momento%20white.png',
  'fairflow': 'https://customer-assets.emergentagent.com/job_e3d607ae-98b3-4cfa-9c06-6cf7ab967614/artifacts/znxi8qvo_FAIRFLOW%20FINAL%20LOGO.png',
  'fromnow': 'https://customer-assets.emergentagent.com/job_e3d607ae-98b3-4cfa-9c06-6cf7ab967614/artifacts/j19rnga9_fromnow.png',
  'nina': 'https://customer-assets.emergentagent.com/job_e3d607ae-98b3-4cfa-9c06-6cf7ab967614/artifacts/od3ttswz_Nina%20nina.png',
  'urban-echoes': 'https://customer-assets.emergentagent.com/job_e3d607ae-98b3-4cfa-9c06-6cf7ab967614/artifacts/9kmwo9xk_Urban%20Echoes%202grispng-04.png',
  'fairtix': 'https://customer-assets.emergentagent.com/job_e3d607ae-98b3-4cfa-9c06-6cf7ab967614/artifacts/y9gb8rqs_fairtix.png',
};

// ZONE A - Main partners with full descriptions
const ZONE_A_PARTNERS = [
  {
    id: 'metro-club',
    badge: 'SPONSORED BY',
    badgeColor: 'cyan',
    title: 'METRO CLUB',
    logo: LOGOS['metro-club'],
    text: [
      'Metro Club is a cultural actor at the heart of the Belgian electronic music ecosystem. Recognized for the quality of its programming and its commitment to contemporary club culture, Metro Club supports projects that place artistic vision, sound exploration and collective experience at the center.',
      'More than a venue, Metro Club acts as a catalyst // in ongoing dialogue with artists, collectives and initiatives that challenge the way music is experienced today.',
      'For Vague Vision Sound, the connection was immediate. A shared interest in immersive formats, a deep respect for artistic integrity, and a vision that goes far beyond a single event. Their support of WITHIN is not based on visibility. It is based on alignment.',
    ],
    cta: '@metroclub.ath',
    ctaUrl: 'https://instagram.com/metroclub.ath',
  },
  {
    id: 'leddis',
    badge: 'SPONSORED BY',
    badgeColor: 'cyan',
    title: 'LEDDIS',
    logo: LOGOS['leddis'],
    text: [
      'LEDDIS is a Belgian company specializing in advanced LED lighting solutions, combining technical precision, energy performance and sustainable engineering. Operating across Belgium and Luxembourg, LEDDIS works at the intersection of innovation, infrastructure and long-term responsibility.',
      'WITHIN is built around perception, space and the subtle dialogue between sound and light. We chose LEDDIS because they embody a vision of light that transcends utility.',
      'LEDDIS chose to support WITHIN because they believe technological excellence should not remain confined to industrial environments // it can also fuel artistic creation and immersive cultural experiences. A partnership rooted in shared ambition: that innovation in infrastructure and innovation in culture can elevate one another.',
    ],
    cta: '@leddis.officiel',
    ctaUrl: 'https://instagram.com/leddis.officiel',
  },
  {
    id: 'momento',
    badge: 'POWERED BY',
    badgeColor: 'muted',
    title: 'MOMENTO',
    logo: LOGOS['momento'],
    text: [
      'Momento is an international initiative committed to creating meaningful spaces where people genuinely connect // through music, art and shared experience.',
      'More than an event platform, Momento builds environments where presence matters, where conversations become collaborations, and where communities grow beyond borders.',
      'Their vision aligns naturally with Vague Vision Sound. WITHIN was never meant to be just another show. It is an immersive gathering point for artists, cultural actors and curious minds seeking something intentional. This collaboration reflects a shared conviction: that culture can transcend geography and become a true meeting ground between worlds.',
    ],
    cta: '@for.momento',
    ctaUrl: 'https://instagram.com/for.momento',
  },
  {
    id: 'fairflow',
    badge: 'POWERED BY',
    badgeColor: 'muted',
    title: 'FAIRFLOW',
    logo: LOGOS['fairflow'],
    isPoetic: true,
    text: [
      'Alongside building Vague Vision Sound, we have been developing something internally.',
      '',
      'A structure. A system. A way.',
      '',
      'Made for artists, by artists.',
      'A system to help artists create without being overwhelmed by the world around them.',
      'A way to make collaboration clearer.',
      '',
      'FairFlow was born from the real needs of VVS itself // from honest questions around contribution, transparency and collective work. The Planetarium experience is the first event powered by this internal framework.',
      '',
      'In progress.',
    ],
    cta: 'fairflow.be',
    ctaUrl: 'https://fairflow.be',
    external: true,
  },
];

// ZONE B - Additional partners with shorter descriptions (grid layout)
const ZONE_B_PARTNERS = [
  {
    id: 'fromnow',
    badge: 'POWERED BY',
    title: 'fromnow',
    logo: LOGOS['fromnow'],
    text: `Passionate about music since 8, fromnow built his universe through projects like Immanence, Ascendance and WAVy // each one rooted in a concept, a mood, a world.

Then everything was stolen. 18 years of work, gone in one moment. He went looking through old emails and forgotten drafts, and built FromZero from what remained. 31 fragments. Not a look back // a point of departure.

Mixing & mastering engineer. Founder of Vague Vision Sound. Co-founder of FairFlow.`,
    cta: '@fromnowmusic',
    ctaUrl: 'https://instagram.com/fromnowmusic',
  },
  {
    id: 'nina',
    badge: 'POWERED BY',
    title: 'Nina',
    logo: LOGOS['nina'],
    text: `Nina has spent years accompanying artists through one of the most overlooked dimensions of a creative career: building a coherent identity in a world that moves fast and forgets faster. She met fromnow and became one of the co-founders of Vague Vision Sound // bringing structure, vision and a deep understanding of what it takes to build something lasting.

Alongside Piotr Paluch, fromnow and Denis Bogaerts, she co-founded Musicpreneurs // a structure dedicated to helping artists understand and build their own ecosystem, whether artistic, financial or legal. Her work has touched artists like fromnow, Lysjoy and Vincent Thibault, among others.

She doesn't just advise. She builds alongside.`,
    cta: '@bynina.vdr',
    ctaUrl: 'https://instagram.com/bynina.vdr',
  },
  {
    id: 'urban-echoes',
    badge: 'POWERED BY',
    title: 'Urban Echoes',
    logo: LOGOS['urban-echoes'],
    text: `Urban Echoes is a Brussels-based techno collective founded in 2025 by Bored Bouddha and fromnow. What started as a tight circle has grown and continues to shape itself // welcoming new members, new energy while staying true to its original vision.

From brutalist venues and unique locations to an international podcast series shining a light on artists from across the globe, their mission has always been the same: make spaces breathe differently, and give the underground a place to exist with intention.

The collective is still building. Still evolving. And that's exactly the point.`,
    cta: '@urban.echoes__',
    ctaUrl: 'https://instagram.com/urban.echoes__',
    websiteUrl: 'https://urbanechoes.org',
  },
  {
    id: 'fairtix',
    badge: 'POWERED BY',
    title: 'Fairtix',
    logo: LOGOS['fairtix'],
    text: `The ticketing infrastructure behind WITHIN.
Simple. Reliable. Built for events that mean something.`,
    cta: 'fairtix.io',
    ctaUrl: 'https://fairtix.io',
    external: true,
  },
];

// Zone A Partner Card Component
function ZoneACard({ partner, isMobile }) {
  return (
    <section
      id={partner.id}
      data-testid={`partner-${partner.id}`}
      style={{
        padding: isMobile 
          ? (partner.isPoetic ? '50px 20px' : '40px 20px')
          : (partner.isPoetic ? '70px 32px' : '60px 32px'),
        borderTop: '1px solid rgba(0, 140, 220, 0.1)',
        background: partner.isPoetic ? 'rgba(0,0,0,0.15)' : 'transparent',
      }}
    >
      <div style={{ maxWidth: '950px', margin: '0 auto' }}>
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '24px' : '36px',
          alignItems: isMobile ? 'center' : 'flex-start',
        }}>
          {/* Logo */}
          {partner.logo && (
            <div style={{
              flex: '0 0 auto',
              width: 'clamp(140px, 18vw, 180px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '24px',
              background: 'linear-gradient(135deg, rgba(8,16,30,0.8) 0%, rgba(5,12,25,0.9) 100%)',
              borderRadius: '8px',
              border: '1px solid rgba(100,160,200,0.08)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            }}>
              <img 
                src={partner.logo} 
                alt={partner.title}
                style={{
                  width: '100%',
                  maxHeight: '90px',
                  objectFit: 'contain',
                  filter: 'brightness(1.05)',
                }}
              />
            </div>
          )}

          {/* Content */}
          <div style={{ flex: 1, minWidth: isMobile ? '0' : '300px', width: '100%', textAlign: isMobile ? 'center' : 'left' }}>
            {/* Badge */}
            <div
              style={{
                display: 'inline-block',
                padding: '4px 12px',
                border: `1px solid ${partner.badgeColor === 'cyan' ? 'rgba(0,240,255,0.4)' : 'rgba(255,255,255,0.2)'}`,
                color: partner.badgeColor === 'cyan' ? 'var(--vvs-cyan)' : 'rgba(255,255,255,0.5)',
                fontFamily: 'Space Grotesk',
                fontSize: '10px',
                letterSpacing: '0.15em',
                marginBottom: '16px',
              }}
            >
              {partner.badge}
            </div>

            {/* Title */}
            <h2
              style={{
                fontFamily: 'Syne, sans-serif',
                fontWeight: 800,
                fontSize: 'clamp(28px, 5vw, 48px)',
                textTransform: 'uppercase',
                color: '#F0F4F8',
                marginBottom: '20px',
                letterSpacing: '-0.01em',
              }}
            >
              {partner.title}
            </h2>

            {/* Text */}
            {partner.isPoetic ? (
              <div style={{
                fontFamily: 'Space Grotesk',
                fontSize: 'clamp(14px, 1.6vw, 16px)',
                lineHeight: 2,
                color: 'var(--vvs-steel)',
              }}>
                {partner.text.map((line, i) => (
                  <p key={i} style={{ margin: 0, minHeight: line === '' ? '16px' : 'auto' }}>
                    {line}
                  </p>
                ))}
              </div>
            ) : (
              <div style={{
                fontFamily: 'Space Grotesk',
                fontSize: 'clamp(13px, 1.5vw, 15px)',
                lineHeight: 1.8,
                color: 'var(--vvs-steel)',
              }}>
                {partner.text.map((para, i) => (
                  <p key={i} style={{ marginBottom: i < partner.text.length - 1 ? '14px' : '0' }}>
                    {para}
                  </p>
                ))}
              </div>
            )}

            {/* CTA */}
            <a
              href={partner.ctaUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`partner-cta-${partner.id}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                marginTop: '24px',
                fontFamily: 'Space Grotesk',
                fontSize: '13px',
                letterSpacing: '0.06em',
                color: 'var(--vvs-cyan)',
                textDecoration: 'none',
                transition: 'opacity 0.3s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.7')}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
            >
              {partner.cta} <ExternalLink size={12} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

// Zone B Partner Card Component (compact grid)
function ZoneBCard({ partner, isMobile }) {
  return (
    <div
      id={partner.id}
      data-testid={`partner-${partner.id}`}
      style={{
        padding: isMobile ? '20px' : '28px',
        background: 'linear-gradient(135deg, rgba(8,16,30,0.6) 0%, rgba(5,12,25,0.7) 100%)',
        borderRadius: '8px',
        border: '1px solid rgba(100,160,200,0.06)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      {/* Header: logo + title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
        {partner.logo && (
          <img 
            src={partner.logo} 
            alt={partner.title}
            style={{
              width: '48px',
              height: '48px',
              objectFit: 'contain',
              filter: 'brightness(1.05)',
            }}
          />
        )}
        <div>
          <p style={{
            fontFamily: 'Space Grotesk',
            fontSize: '9px',
            letterSpacing: '0.12em',
            color: 'rgba(255,255,255,0.4)',
            margin: '0 0 4px',
            textTransform: 'uppercase',
          }}>
            {partner.badge}
          </p>
          <h3 style={{
            fontFamily: 'Syne, sans-serif',
            fontWeight: 700,
            fontSize: '18px',
            color: '#F0F4F8',
            margin: 0,
            textTransform: 'uppercase',
          }}>
            {partner.title}
          </h3>
        </div>
      </div>

      {/* Text */}
      <div style={{
        fontFamily: 'Space Grotesk',
        fontSize: '13px',
        lineHeight: 1.7,
        color: 'var(--vvs-steel)',
        flex: 1,
        whiteSpace: 'pre-line',
      }}>
        {partner.text}
      </div>

      {/* CTA */}
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '16px' }}>
        {partner.websiteUrl && (
          <a
            href={partner.websiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontFamily: 'Space Grotesk', fontSize: '12px', letterSpacing: '0.06em', color: 'rgba(240,244,248,0.55)', textDecoration: 'none', transition: 'opacity 0.3s' }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.8')}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
          >
            urbanechoes.org <ExternalLink size={10} />
          </a>
        )}
        <a
          href={partner.ctaUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontFamily: 'Space Grotesk', fontSize: '12px', letterSpacing: '0.06em', color: 'var(--vvs-cyan)', textDecoration: 'none', transition: 'opacity 0.3s' }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.7')}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
        >
          {partner.cta} <ExternalLink size={11} />
        </a>
      </div>
    </div>
  );
}

export default function PartnersPage() {
  const heroRef = useRef(null);
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    document.title = 'Partners // VVS';
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced || !heroRef.current) return;

    anime
      .timeline({ easing: 'easeOutExpo' })
      .add({ targets: '.partners-caption', translateY: [20, 0], opacity: [0, 1], duration: 700 })
      .add({ targets: '.partners-title', translateY: [50, 0], opacity: [0, 1], duration: 1000 }, '-=400')
      .add({ targets: '.partners-sub', translateY: [20, 0], opacity: [0, 1], duration: 600 }, '-=600');
  }, []);

  // Scroll to anchor on mount
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 400);
    }
  }, [location]);

  return (
    <div style={{ position: 'relative', zIndex: 1, paddingTop: '64px', overflowX: 'hidden' }}>
      {/* Hero */}
      <section
        ref={heroRef}
        data-testid="partners-hero"
        style={{
          minHeight: isMobile ? 'auto' : '40vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          padding: isMobile ? '60px 20px 50px' : '70px 32px 50px',
          position: 'relative',
          background: `linear-gradient(to bottom, rgba(2, 8, 20, 0.6) 0%, rgba(2, 8, 20, 0.85) 60%, #020814 100%), url(${WAVE_BG}) center center / cover no-repeat`,
        }}
      >
        <div style={{ maxWidth: '900px', margin: '0 auto', width: '100%', textAlign: 'center' }}>
          <p className="partners-caption caption" style={{ marginBottom: '16px', opacity: 0, color: 'var(--vvs-cyan)' }}>
            The people behind the vision
          </p>
          <h1 className="partners-title neon-heading" style={{
            fontSize: isMobile ? '42px' : 'clamp(48px, 10vw, 100px)',
            lineHeight: 0.9,
            letterSpacing: '-0.02em',
            textTransform: 'uppercase',
            margin: '0 0 20px',
            opacity: 0,
          }}>
            PARTNERS
          </h1>
          <p className="partners-sub" style={{
            fontFamily: 'Space Grotesk',
            fontSize: isMobile ? '14px' : 'clamp(14px, 1.6vw, 17px)',
            color: 'var(--vvs-steel)',
            opacity: 0,
            maxWidth: '460px',
            margin: '0 auto',
            lineHeight: 1.6,
            padding: '0 10px',
          }}>
            They don't just support the project.<br />
            They share the vision.
          </p>
        </div>
      </section>

      {/* Zone A - Main partners */}
      {ZONE_A_PARTNERS.map((partner) => (
        <ZoneACard key={partner.id} partner={partner} isMobile={isMobile} />
      ))}

      {/* Zone B - Additional partners grid */}
      <section style={{ padding: isMobile ? '40px 20px' : '60px 32px', borderTop: '1px solid rgba(0, 140, 220, 0.1)' }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <p className="caption" style={{ marginBottom: '40px', textAlign: 'center' }}>
            Also powered by
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: isMobile ? '16px' : '24px',
          }}>
            {ZONE_B_PARTNERS.map((partner) => (
              <ZoneBCard key={partner.id} partner={partner} isMobile={isMobile} />
            ))}
          </div>
        </div>
      </section>

      {/* Back to top */}
      <section style={{ padding: isMobile ? '40px 20px 100px' : '40px 32px 70px', textAlign: 'center' }}>
        <MagneticButton
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="btn-metallic"
          style={{ cursor: 'pointer' }}
          data-testid="partners-back-top"
        >
          ↑ Back to top
        </MagneticButton>
      </section>
    </div>
  );
}
